import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import cn from 'classnames'
import { HTMLAttributes, memo, useRef } from 'react'
import { config, animated, useTransition } from 'react-spring'
import { useIsomorphicLayoutEffect } from 'react-use'

import styles from './Drawer.module.scss'
import { getDirectionTransitionConfig } from './utils'

import usePortalRoots from '~lib/hooks/usePortalRoots'
import Overlay from '~lib/ui-kit/Overlay'
import Portal from '~lib/ui-kit/Portal'

interface DrawerProps extends HTMLAttributes<HTMLDivElement> {
  opened: boolean
  onOverlayClick?: (ev: React.MouseEvent<HTMLElement>) => void
  size: number | string
  direction: 'left' | 'right'
  overlayVariant?: 'blur' | 'none'
  overlayClassName?: string
}

function Drawer({
  opened,
  onOverlayClick,
  size,
  direction,
  overlayVariant = 'blur',
  overlayClassName,
  className,
  children,
  ...props
}: React.PropsWithChildren<DrawerProps>) {
  const { modalRoot } = usePortalRoots()

  const transition = useTransition(opened, {
    config: { ...config.gentle, clamp: true },
    ...getDirectionTransitionConfig({ direction, size }),
  })

  const drawerRef = useRef<HTMLElement>(null)

  useIsomorphicLayoutEffect(() => {
    if (opened && drawerRef.current) {
      const node = drawerRef.current

      disableBodyScroll(node, { reserveScrollBarGap: true })
      return () => enableBodyScroll(node)
    }
    return undefined
  }, [opened])

  return (
    <Portal root={modalRoot}>
      {transition((style, mounted) => {
        return (
          mounted && (
            <>
              {overlayVariant !== 'none' && (
                <Overlay show={opened} position="fixed" variant={overlayVariant} onClick={onOverlayClick} />
              )}

              <animated.aside
                ref={drawerRef}
                style={style}
                className={cn(styles.root, styles[direction], className)}
                {...props}
              >
                {children}
              </animated.aside>
            </>
          )
        )
      })}
    </Portal>
  )
}

export default memo(Drawer)
